"use client";

import EnqueryForm from "@/components/forms/EnqueryForm";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { CircleCheck } from "lucide-react";
import useMilesOneDeepLink from "@/hooks/useMilesOneDeepLink";

type CairaBannerProps = {
  /** Don't render the enquiry form (e.g. /caira-institutions, which gates the page itself). */
  hideForm?: boolean;
  /** With hideForm, show the thank-you card in the form's place. */
  showThankYou?: boolean;
};

const CairaBanner_New = ({
  hideForm = false,
  showThankYou = false,
}: CairaBannerProps) => {
  const { tryOpenApp } = useMilesOneDeepLink();

  return (
    <>
      <div
        className="w-full bg-center relative bg-[#0D1B2A]"
        style={{ minHeight: "580px", contain: "layout" }}
      >
        {/* Desktop Background */}
        <div className="hidden md:block absolute inset-0 top-0">
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/caira/new/CAIRABG.webp"
            alt="CAIRA Banner Desktop Background"
            fill
            className="object-cover z-0"
            priority
          />
        </div>
        {/* Mobile Background */}
        <div className="md:hidden absolute inset-0 top-0">
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/caira/new/CAIRABG_mob.webp"
            alt="CAIRA Banner Mobile Background"
            fill
            className="object-cover z-0"
            priority
          />
        </div>

        <div
          className="lg:container mx-auto px-4 md:px-6 flex flex-col gap-6 md:gap-8 md:flex-row 
        justify-between items-center relative py-8 md:py-16"
        >
          <div className="flex flex-col items-center md:items-start text-center md:text-left gap-3 md:gap-4 w-full md:w-[55%]">
            <div className="flex flex-col items-center md:items-start">
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/caira/new/new-caira-logo.webp"
                alt="CAIRA"
                width={268}
                height={50}
                className="h-[42px] md:h-[64px] w-auto object-contain"
                priority
              />

              <p className="text-[#fff] text-sm md:text-[22px] font-bold">
                Certified AI Ready Accountant
              </p>
            </div>
            <div className="leading-[1.2] md:leading-[1] md:mt-4">
              <h1 className="text-white font-bold font-sans text-[32px] md:text-[48px] xl:text-[52px]">
                The AI Credential
              </h1>

              <h2 className="text-[#FFBA08] font-semibold text-[20px] md:text-[28px] xl:text-[30px]">
                Built for Accountants
              </h2>
            </div>

            <div className="flex divide-x divide-[#606e96] w-full md:w-auto rounded-[19.68px] border-[0.82px] border-[#606e96] bg-[rgba(33,100,167,0.10)] shadow-[inset_0_0_45.018px_rgba(166,137,255,0.30)] backdrop-blur-[4.92px] mt-2 max-w-[430px]">
              <p className="text-[#8FC4FF] text-xs md:text-sm font-medium text-center px-2 py-2.5 md:px-4">
                NASBA-approved CPE Credits
              </p>
              <p className="text-[#8FC4FF] text-xs md:text-sm font-medium text-center px-2 py-2.5 md:px-4">
                Build AI Agents &amp; Workflows
              </p>
              <p className="text-[#8FC4FF] text-xs md:text-sm font-medium text-center px-2 py-2.5 md:px-4">
                Hands-on practice in Miles AI Labs
              </p>
            </div>

            {/* Badge + Showcase on LinkedIn */}
            <div className="hidden md:block mt-2">
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/caira/new/cairaBedgePC.webp"
                alt="AI in Accounting 101 Badge - Showcase on LinkedIn"
                width={280}
                height={240}
                className="w-[280px] h-auto object-contain"
              />
            </div>
            <div className="md:hidden mt-2 flex justify-end w-full">
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/caira/new/cairaBedgeMob.webp"
                alt="AI in Accounting 101 Badge - Showcase on LinkedIn"
                width={220}
                height={192}
                className="w-full max-w-[220px] h-auto object-contain"
              />
            </div>
          </div>

          {/* Form Section */}
          {hideForm ? (
            showThankYou && (
              <div className="md:w-[400px] w-full p-4 rounded-[19.68px] border-[0.82px] border-[#606e96] bg-[rgba(33,100,167,0.10)] shadow-[inset_0_0_45.018px_rgba(166,137,255,0.30)] backdrop-blur-[4.92px]">
                <div className="hidden md:block">
                  <div className="w-full pb-4 flex justify-center">
                    <CircleCheck className="w-10 h-10 text-blue-500" />
                  </div>
                  <p
                    className={`rounded-md md:text-[18px] font-bold text-center text-white`}
                  >
                    {/* Your online meet is confirmed! */}
                    Thank you! Your details have been received.
                  </p>
                  <div className="text-center">
                    <p className={` mt-2 text-base font-semibold text-white`}>
                      {/* You’re all set. Webinar details have been sent to your Email &
                  WhatsApp. */}
                      One of our academic experts
                      <span style={{ display: "block" }}>
                        will reach out to you shortly.
                      </span>
                    </p>
                  </div>
                  <div className="rounded-[20px] bg-[linear-gradient(180deg,_#0B2A4D_0%,_#26467A_100%)] p-5 mt-4">
                    <div className="text-center">
                      <p className="font-bold text-white mt-2 leading-[100%] text-base w-full">
                        Get Miles One App
                      </p>
                      <p className="font-normal text-white mt-2 leading-[100%] text-xs w-full">
                        The #1 App for AI-Ready Accountants
                      </p>
                    </div>
                    <div className="flex justify-center items-center gap-3 mt-4">
                      <div className="flex flex-col gap-2">
                        <Link
                          href="https://apps.apple.com/us/app/miles-us-pathway/id6504799221"
                          target="_blank"
                          className="flex items-center gap-2 px-2 py-2 rounded-[8px] bg-white/10 w-[130px] border-[1px] border-white/10"
                        >
                          <Image
                            src="https://ik.imagekit.io/mileseducation/miles_website/home/app-store.webp"
                            alt="App Store"
                            width={24}
                            height={24}
                            className="cursor-pointer"
                          />
                          <div className="text-white text-left leading-tight">
                            <p className="text-[10px]">Download on the</p>
                            <p className="text-[16px] font-medium">App Store</p>
                          </div>
                        </Link>

                        <Link
                          href="https://play.google.com/store/apps/details?id=com.miles.one"
                          target="_blank"
                          className="flex items-center gap-2 px-2 py-2 rounded-[8px] bg-white/10 w-[130px] border-[1px] border-white/10"
                        >
                          <Image
                            src="https://ik.imagekit.io/mileseducation/miles_website/home/play-store.webp"
                            alt="Play Store"
                            width={30}
                            height={30}
                            className="cursor-pointer"
                          />
                          <div className="text-white text-left leading-tight">
                            <p className="text-[10px]">GET IT ON</p>
                            <p className="text-[16px] font-medium">
                              Play Store
                            </p>
                          </div>
                        </Link>
                      </div>
                      <div className="flex flex-col items-center justify-center mt-1 cursor-pointer">
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/home/milesone_qr_code.webp"
                          alt="QR Code"
                          width={95}
                          height={95}
                          className=""
                        />
                        <p className="text-[10px] text-[#EEEEEE]">
                          Scan to download
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="md:hidden">
                  <div className="w-full pb-4 flex justify-center">
                    <CircleCheck className="w-10 h-10 text-blue-500" />
                  </div>
                  <p
                    className={`rounded-md font-semibold text-center text-white`}
                  >
                    {/* Your online meet is confirmed! */}
                    Thank you! Your details have been received.
                  </p>
                  <div className="text-center">
                    <p className={`font-semibold mt-2 text-sm text-white`}>
                      {/* You’re all set. Webinar details have been sent to your Email &
                  WhatsApp. */}
                      One of our academic experts
                      <span style={{ display: "block" }}>
                        will reach out to you shortly.
                      </span>
                    </p>
                  </div>
                  <div className="mt-4 bg-[linear-gradient(94deg,_#061A2F_0.32%,_#135295_99.92%)] rounded-xl p-3">
                    <div className="flex gap-3 items-center">
                      <Image
                        src="/miles_logo_for_form.png"
                        alt="App Store"
                        width={24}
                        height={14}
                        className="cursor-pointer h-4"
                      />
                      <p className="text-base font-semibold text-white">
                        Download the MilesOne App now!
                      </p>
                    </div>
                    <button
                      className="w-full px-1 py-2 justify-center rounded-[12px_12px_12px_0px] mt-3 flex bg-white text-[#316BC9] font-semibold"
                      onClick={() => {
                        tryOpenApp();
                      }}
                    >
                      Open
                    </button>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="md:w-[400px] w-full p-4 rounded-[19.68px] border-[0.82px] border-[#606e96] bg-[rgba(33,100,167,0.10)] shadow-[inset_0_0_45.018px_rgba(166,137,255,0.30)] backdrop-blur-[4.92px]">
              <EnqueryForm
                pageName="CAIRA"
                courseId={1}
                formHeading={FormHeading.MONTHLY_WEBINAR}
                formType="page"
                isAStepperForm
                subCategory={FinanceAndAccountingSubcategory.CPA}
                submitButtonName="Register for Webinar"
                successMessage={SuccessMessageType.ACCOUNTING}
                verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                darkBackground
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CairaBanner_New;
